import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { get as _get } from 'lodash';
import { format as D3format } from 'd3';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/GetApp';

import SearchBar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import ReportsService, { HistoryService, NestedSearch, reportsFields, historyFields } from '../../../data/reports-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import Helpers from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';
import {LoadingOverlay, NoRowsOverlay} from '../../../components/xgrid/overlays';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
}));

const Reports = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const downloadToastRef = useRef(null);
    const { height } = useWindowSize();
    const [reports, setReports] = useState([]);
    const [history, setHistory] = useState([]);
    const [allreports, setAllReports] = useState([]);
    const [allhistory, setAllHistory] = useState([]);
    const [reportsInit, setReportsInit] = useState(false);
    const [historyInit, setHistoryInit] = useState(false);
    const [deleteReportDialog, setDeleteReportDialog] = useState(false);
    const [deleteHistoryDialog, setDeleteHistoryDialog] = useState(false);
    const [deleteAllHistoryDialog, setDeleteAllHistoryDialog] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [downloadHistoryDialog, setDownloadHistoryDialog] = useState(false);
    const reportTableAPIRef = useApiRef();
    const historyTableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [reportColumnWidth, setReportColumnWidth] = useState({});
    const [historyColumnWidth, setHistoryColumnWidth] = useState({});
    const [reportSortModel, setReportSortModel] = useState([{ field: 'data.title', sort: 'asc' }]);
    const [historySortModel, setHistorySortModel] = useState([{ field: 'created', sort: 'desc' }]);
    const reportsPerms = Auth.permissions.get('Reports');
    const alertPerm = Auth.permissions.get('Alerts').read;
    const metadataPerm = Auth.permissions.get('Metadata').read;
    const sensorPerm = Auth.permissions.get('Sensors').read;
    const auditPerm = Auth.permissions.get('Audit').read;
    const suriPerm = Auth.permissions.get('SuricataRulesPolicy').read;

    // Search state
    const [reportSearch, setReportSearch] = useState('');
    const [historySearch, setHistorySearch] = useState('');

    useEffect(() => {
        getReports();
        getHistory();

        setReportColumnWidth(getProfile('reports').reportWidths || {});
        setHistoryColumnWidth(getProfile('reports').historyWidths || {});
    }, [getProfile]);

    const handleColumnResize = params => {
        const widths = reportTableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('reports', {
            ...getProfile('reports'),
            reportWidths: widths,
        });
        setReportColumnWidth(widths);
    };

    const handleReportColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = reportTableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('reports', {
                ...getProfile('reports'),
                reportColumns: order,
            });
        });
    };

    const handleHistoryColumnResize = params => {
        const widths = historyTableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('reports', {
            ...getProfile('reports'),
            historyWidths: widths,
        });
        setHistoryColumnWidth(widths);
    };

    const handleHistoryColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = historyTableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('reports', {
                ...getProfile('reports'),
                historyColumns: order,
            });
        });
    };

    const getReports = () => {
        ReportsService.getReports().then(res => {
            const reports = res.data.map(v => { return { ...v, id: Helpers.UniqueID() } });
            setAllReports(reports);
        });
    };

    const getHistory = () => {
        HistoryService.getHistory().then(res => {
            setAllHistory(res.data);
        });
    };

    const reportFormatType = (format) => {
        if (format === 'rst') {
            return 'Plain Text';
        }
        return format.toUpperCase();
    };

    const tableRenderers = {
        schedule: d => {
            return <div>{`${Helpers.capitalize(d.row.data.frequency.slice(1))} ${d.row.data.frequency_other ? d.row.data.frequency_other : ''}`}</div>;
        },
        type: d => {
            return <div>{Helpers.capitalize(d.row.data.id)}</div>;
        },
        recipients: d => {
            const rec = Array.prototype.concat(d.row.data.recipients, d.row.data.additional_recipients).map((v, i) => {
                return <span key={'report-recipient-' + i}>{v}</span>;
            });

            if (rec.length) {
                return (
                    <div>
                        {rec.slice(1).reduce((xs, x, i) => {
                            return xs.concat([', ', x]);
                        }, [rec[0]])}
                    </div>
                );
            } else {
                return (<div>(none, build only)</div>);
            }
        },
        formats: d => {
            const f = Object.keys(d.row.data.attachments).filter((v) => d.row.data.attachments[v]);
            return (
                <div>
                    {f.map((v, i) => {
                        return reportFormatType(v) + (i === f.length - 1 ? '' : ', ');
                    })}
                </div>
            );
        },
        created: d => {
            const date = Moment(d.row.created);
            return date.isValid() ? date.format(theme.date.long) : '';
        },
        finished: d => {
            const date = Moment(d.row.finished && d.row.finished.Time);
            return date.isValid() ? date.format(theme.date.long) : '';
        },
        status: d => { return <div>{d.row.data.status}</div> },
        size: d => { return <div>{Helpers.formatBytes(d.row.data.size || 0)}</div> },
        report: d => { return <div>{(d.row.report_data && d.row.report_data.title) || (d.row.data && d.row.data.title)}</div> },
        delivered_to: d => {
            const r = d.row.report_data.recipients || [];
            const ar = d.row.report_data.additional_recipients || [];
            const rec = Array.prototype.concat(r, ar).map((v, i) => {
                return <span key={'report-recipient-' + i}>{v}</span>;
            });

            if (rec.length) {
                return (
                    <div>
                        {rec.slice(1).reduce((xs, x, i) => {
                            return xs.concat([', ', x]);
                        }, [rec[0]])}
                    </div>
                );
            } else {
                return (<div>(none, build only)</div>);
            }
        },
    };

    const reportsColumns = reportsFields.map(f => {
        return {
            ...f,
            width: reportColumnWidth[f.headerName] || 0,
            ...(!reportColumnWidth[f.headerName] ? { flex: 1 } : {}),
            valueGetter: d => _get(d, `row.${f.field}`, ''),
            renderCell: tableRenderers[f.variable],
        };
    });

    const historyColumns = historyFields.map(f => {
        return {
            ...f,
            width: historyColumnWidth[f.headerName] || 0,
            ...(!historyColumnWidth[f.headerName] ? { flex: 1 } : {}),
            valueGetter: d => _get(d, `row.${f.field}`, ''),
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleCreateReport = () => {
        navigate('/system/reports/create');
    };

    const handleSelectReport = (s) => {
        setSelectedReport(s.row);
    };

    const handleSelectHistory = (s) => {
        setSelectedHistory(s.row);
    };

    const handleEditReport = () => {
        navigate(`/system/reports/update?r=${btoa(JSON.stringify(selectedReport))}`);
    };

    const handleReportSearchChange = ({ search }) => {
        setReportSearch(search);
    };

    const handleHistorySearchChange = ({ search }) => {
        setHistorySearch(search);
    };

    const canDelete = report => {
        const typ = report.report_data ? report.report_data.id : report.data.id;
        switch (typ) {
            case 'alerts': return alertPerm;
            case 'metadata': return metadataPerm;
            case 'sensors': return sensorPerm;
            case 'audit': return auditPerm;
            case 'suricy': return suriPerm;
            default: return false;
        }
    };

    const handleCloseDeleteReportDialog = () => {
        setDeleteReportDialog(false);
        setTimeout(() => {
            setSelectedReport(null);
        }, 1000);
    };

    const handleCloseDeleteHistoryDialog = () => {
        setDeleteHistoryDialog(false);
        setTimeout(() => {
            setSelectedHistory(null);
        }, 1000);
    };

    const handleCloseDeleteAllHistoryDialog = () => {
        setDeleteAllHistoryDialog(false);
    };

    const handleCloseDownloadHistoryDialog = () => {
        setTimeout(() => {
            setSelectedHistory(null);
        }, 1000);
        setDownloadHistoryDialog(false);
    };

    const handleOpenDeleteReport = () => {
        setDeleteReportDialog(true);
    };

    const handleOpenDeleteHistory = () => {
        setDeleteHistoryDialog(true);
    };

    const handleOpenDeleteAllDialog = () => {
        setDeleteAllHistoryDialog(true);
    };

    const handleOpenDownloadHistoryDialog = () => {
        setDownloadHistoryDialog(true);
    };

    const handleDeleteReport = () => {
        ReportsService.deleteReport(selectedReport.uuid).then(() => {
            toast.success('Report template deleted successfully!');
            // setReportsTable(false);
            setSelectedReport(null);
            setDeleteReportDialog(false);
            getReports(reportSearch);
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
        });
    };

    const handleDeleteHistory = () => {
        HistoryService.deleteHistory(selectedHistory.report_uuid, selectedHistory.id).then(() => {
            toast.success('Report deleted from History');
            setSelectedHistory(null);
            setDeleteHistoryDialog(false);
            getHistory();
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
            setSelectedHistory(null);
            setDeleteHistoryDialog(false);
        });
    };

    const handleRunReport = () => {
        toast.info('Creating report template and generating report...');
        ReportsService.runReport(selectedReport.uuid).then(() => {
            toast.success('Report created successfully!');
            getHistory();
            setTabs(1);
        });
    };

    const handleDownloadHistory = (format, created) => {
        HistoryService.downloadReportFromHistory(selectedHistory.report_uuid, selectedHistory.id, format).then((res) => {
            downloadToastRef.current = toast(<React.Fragment>
                <Box display="flex" alignItems="center">
                    <Typography>Report ready for download</Typography>
                    <Link
                        className={classes.linkRight}
                        target="_blank"
                        download={`report-${created}.${format === 'rst' ? 'txt' : format}`}
                        href={res}
                    >
                        <IconButton
                            aria-label="download history"
                            onClick={() => { toast.dismiss(downloadToastRef.current) }}
                            size="large">
                            <DownloadIcon/>
                        </IconButton>
                    </Link>
                </Box>
            </React.Fragment>, {
                autoClose: 30000,
            });
        });
        handleCloseDownloadHistoryDialog();
    };

    const handleDeleteAllHistory = () => {
        if (historySearch.length > 0) {
            const uuids = (history || []).map(h => h.report_uuid);
            HistoryService.deleteSomeHistory(uuids).then(() => {
                toast.success('Report history deleted', {
                    autoClose: 5000,
                });
                handleRefreshHistory();
            });
        } else {
            HistoryService.deleteAllHistory().then(() => {
                toast.success('Report history deleted', {
                    autoClose: 5000,
                });
                handleRefreshHistory();
            });
        }
        handleCloseDeleteAllHistoryDialog();
    };

    const handleRefreshHistory = () => {
        getHistory();
    };

    const handleReportSortModelChange = (sortModel) => {
        setReportSortModel(sortModel);
    };

    const handleHistorySortModelChange = (sortModel) => {
        setHistorySortModel(sortModel);
    };

    useEffect(() => {
        if (!reportsInit) {
            setReportsInit(true);
            return;
        }

        const results = [];
        const reports = allreports;
        (reports || []).forEach((v) => {
            NestedSearch(v, v, results, reportSearch);
        });
        setReports(results);
    }, [reportSearch, allreports, reportsInit]);

    useEffect(() => {
        if (!historyInit) {
            setHistoryInit(true);
            return;
        }

        const results = [];
        const reports = allhistory;
        (reports || []).forEach((v) => {
            NestedSearch(v, v, results, historySearch);
        });
        setHistory(results);
    }, [historySearch, allhistory, historyInit]);

    const [tab, setTabs] = useState(0);
    const handleTabChange = (e, t) => {
        setTabs(t);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box className={classes.fullWidth} hidden={tab !== 0}>
                    <Box display="flex" justifyContent="space-between">
                        <SearchBar immediate onSearch={handleReportSearchChange} searchservice={ReportsService}/>
                        {reportsPerms.admin && <Box><Button variant="contained" aria-label="create report" onClick={handleCreateReport}><AddIcon/> Create Report</Button></Box>}
                    </Box>
                </Box>
                <Box className={classes.fullWidth} hidden={tab !== 1}>
                    <Box display="flex" justifyContent="space-between">
                        <SearchBar immediate onSearch={handleHistorySearchChange} searchservice={HistoryService}/>
                        <Box>
                            <Button variant="contained" className={classes.buttonRight} aria-label="refresh" onClick={handleRefreshHistory}><RefreshIcon/> Refresh</Button>
                            {reportsPerms.delete &&
                                <Button
                                    disabled={!history.length}
                                    variant="contained"
                                    className={classes.buttonRight}
                                    aria-label="delete all"
                                    onClick={handleOpenDeleteAllDialog}>
                                    <DeleteIcon/>
                                    {historySearch && history.length
                                        ? `Delete ${D3format(',.0f')(history.length)} reports`
                                        : 'Delete all'
                                    }
                                </Button>
                            }
                        </Box>
                    </Box>
                </Box>
                <AppBar position="static" className={classes.tabBar}>
                    <Tabs value={tab} onChange={handleTabChange}>
                        <Tab aria-label="Reports" label="Reports" value={0}/>
                        <Tab aria-label="History" label="History" value={1}/>
                    </Tabs>
                </AppBar>
                <Box className={classes.fullWidth} value={tab} index={0} hidden={tab !== 0} height={height - 300} style={{ minHeight: height - 300 }}>
                    <DataGridPro
                        apiRef={reportTableAPIRef}
                        components={{
                            Header: !(reportsPerms.write || reportsPerms.admin || reportsPerms.delete) ? undefined : () => {
                                return (
                                    <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                        {reportsPerms.write && <Button disabled={!selectedReport} className={classes.buttonRight} variant="contained" color="primary" aria-label="run report" onClick={handleRunReport}>Run and Send</Button>}
                                        {reportsPerms.admin && <IconButton
                                            disabled={!selectedReport}
                                            className={classes.buttonRight}
                                            variant="contained"
                                            color="primary"
                                            aria-label="edit report"
                                            onClick={handleEditReport}
                                            size="large"><EditIcon/></IconButton>}
                                        {reportsPerms.admin && <IconButton
                                            disabled={!selectedReport}
                                            className={classes.buttonRight}
                                            variant="contained"
                                            color="secondary"
                                            aria-label="delete report"
                                            onClick={handleOpenDeleteReport}
                                            size="large"><DeleteIcon/></IconButton>}
                                    </Box>
                                );
                            },
                            LoadingOverlay:LoadingOverlay,
                            NoRowsOverlay:NoRowsOverlay
                        }}
                        rows={reports}
                        columns={(() => {
                            const p = getProfile('reports');
                            let c = reportsColumns;

                            if (p.reportColumns && p.reportColumns.length) {
                                c = p.reportColumns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                    return null;
                                });
                            }

                            return c;
                        })()}
                        disableMultipleSelection
                        disableMultipleColumnsFiltering
                        disableMultipleColumnsSorting
                        disableColumnMenu
                        disableVirtualization
                        onRowClick={handleSelectReport}
                        sortingOrder={['desc', 'asc']}
                        sortModel={reportSortModel}
                        onSortModelChange={handleReportSortModelChange}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleReportColumnReorder}
                    />
                </Box>
                <Box className={classes.fullWidth} value={tab} index={1} hidden={tab !== 1} height={height - 310}>
                    <DataGridPro
                        apiRef={historyTableAPIRef}
                        components={{
                            Header: () => {
                                return (
                                    <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                        {reportsPerms.read && <IconButton
                                            disabled={!selectedHistory}
                                            variant="contained"
                                            color="primary"
                                            aria-label="download history"
                                            onClick={handleOpenDownloadHistoryDialog}
                                            size="large"><DownloadIcon/></IconButton>}
                                        {reportsPerms.delete && <IconButton
                                            disabled={!selectedHistory || !canDelete(selectedHistory)}
                                            variant="contained"
                                            color="secondary"
                                            aria-label="delete history"
                                            onClick={handleOpenDeleteHistory}
                                            size="large"><DeleteIcon/></IconButton>}
                                    </Box>
                                );
                            },
                            LoadingOverlay:LoadingOverlay,
                            NoRowsOverlay:NoRowsOverlay
                        }}
                        rows={history}
                        // the issue was the value getters were wrong but masked by the correct table renderers
                        // so data was rendered correctly but sorting needs correct value
                        // programming is a fun and rewarding occupation
                        columns={(() => {
                            const p = getProfile('reports');
                            let c = historyColumns;

                            if (p.historyColumns && p.historyColumns.length) {
                                c = p.historyColumns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                    return null;
                                });
                            }

                            return c;
                        })()}
                        disableMultipleSelection
                        disableMultipleColumnsFiltering
                        disableMultipleColumnsSorting
                        disableColumnMenu
                        disableVirtualization
                        onRowClick={handleSelectHistory}
                        sortingOrder={['desc', 'asc']}
                        sortModel={historySortModel}
                        onSortModelChange={handleHistorySortModelChange}
                        onColumnResize={handleHistoryColumnResize}
                        onColumnOrderChange={handleHistoryColumnReorder}
                    />
                </Box>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteReportDialog}
                onOK={handleDeleteReport}
                open={deleteReportDialog}
                message={`Are you sure you want to delete the report template ${selectedReport ? selectedReport.data.title : ''}?`}
                title="Delete Report Template"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteHistoryDialog}
                onOK={handleDeleteHistory}
                open={deleteHistoryDialog}
                message={`Are you sure you want to delete ${selectedHistory ? selectedHistory.report_data.title : ''} from the reports history?`}
                title="Delete History"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteAllHistoryDialog}
                onOK={handleDeleteAllHistory}
                open={deleteAllHistoryDialog}
                message={'Are you sure you want to delete all reports history?'}
                title="Delete All History"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDownloadHistoryDialog}
                onOK={handleDownloadHistory}
                open={downloadHistoryDialog}
                message={<React.Fragment>
                    {selectedHistory && Object.keys(selectedHistory.report_data.attachments).map((v, i) => {
                        if (v !== 'pdf' && selectedHistory.report_data.attachments[v]) {
                            return (
                                <Button
                                    key={'report-history-download-' + i}
                                    aria-label="download"
                                    onClick={() => { handleDownloadHistory(v, selectedHistory.created) } }>{reportFormatType(v)}
                                </Button>
                            );
                        }
                        return null;
                    })}
                </React.Fragment>}
                title="Download Report"
                cancelLabel="Cancel"
                nook
            />
        </Grid>
    );
};

export default Reports;
